import {
  fetchAppVersion as fetchAppVersionFromLib,
  getAppVersionPlatform,
  getBundledAppVersion,
} from '@/lib/appVersion';
import { logger } from '@/lib/logger';
import { isVersionOlder } from '@/utils/versionUtils';
import { usePathname } from 'expo-router';
import * as Updates from 'expo-updates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

export const CURRENT_VERSION = getBundledAppVersion();
const POLLING_INTERVAL = 15 * 60 * 1000;

export const useAppVersion = () => {
  const [pendingUpgrade, setPendingUpgrade] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const [showStoreUpdateModal, setShowStoreUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<{
    isMandatory: boolean;
    latestVersion: string;
    releaseNotes: string | null;
  } | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  const appState = useRef(AppState.currentState);

  const fetchAppVersion = useCallback(async () => {
    return fetchAppVersionFromLib(getAppVersionPlatform());
  }, []);

  const checkForUpdates = useCallback(async () => {
    try {
      const versionInfo = await fetchAppVersion();

      if (!versionInfo) {
        return;
      }

      const { current_version: latestVersion, min_version: minVersion, release_notes: releaseNotes } = versionInfo;

      const updateRequired = isVersionOlder(CURRENT_VERSION, minVersion);
      const updateAvailable = isVersionOlder(CURRENT_VERSION, latestVersion);

      if (updateRequired || updateAvailable) {
        const isMandatory = updateRequired;
        
        logger.info('Update detected (silent):', {
          currentInApp: CURRENT_VERSION,
          latestInDB: latestVersion,
          type: isMandatory ? 'mandatory' : 'optional',
        });

        setPendingUpgrade(true);
        setIsPolling(false);
        
        setUpdateInfo({
          isMandatory,
          latestVersion,
          releaseNotes: releaseNotes ?? null,
        });
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        logger.info('Stopped polling - will reload on next navigation');
        return;
      }

    } catch (err) {
      logger.error('Error checking for updates:', err);
    }
  }, [fetchAppVersion]);

  useEffect(() => {
    checkForUpdates();

    if (!isPolling) {
      logger.debug('Polling disabled - update detected');
      return;
    }

    pollingIntervalRef.current = setInterval(() => {
      logger.debug('Polling for updates...');
      checkForUpdates();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [checkForUpdates, isPolling]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (pathname !== previousPathname.current) {
      const hasNavigated = previousPathname.current !== pathname;
      
      logger.debug('Route changed (web):', {
        from: previousPathname.current,
        to: pathname,
        pendingUpgrade,
      });

      previousPathname.current = pathname;

      if (pendingUpgrade && hasNavigated && typeof window !== 'undefined') {
        logger.info('[WEB] Pending upgrade detected - reloading app after navigation');
        
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  }, [pathname, pendingUpgrade]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      if (
        pendingUpgrade &&
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        logger.info('[MOBILE] Pending upgrade detected - app came to foreground');

        if (__DEV__) {
          logger.debug('[MOBILE] In development mode - skipping OTA update check');
          logger.info('[MOBILE] Update available - user should download new version from store');
          return;
        }

        try {
          if (Updates.isEnabled) {
            logger.info('[MOBILE] Checking for OTA updates via EAS');
            
            const update = await Updates.checkForUpdateAsync();
            
            if (update.isAvailable) {
              logger.info('[MOBILE] OTA update available - downloading');
              await Updates.fetchUpdateAsync();
              logger.info('[MOBILE] OTA update downloaded - reloading app');
              await Updates.reloadAsync();
              return;
            } else {
              logger.info('[MOBILE] No OTA update - user needs to update via store');
              setShowStoreUpdateModal(true);
            }
          } else {
            logger.info('[MOBILE] EAS Updates not enabled - user needs to update via store');
            setShowStoreUpdateModal(true);
          }
        } catch (error) {
          logger.error('[MOBILE] Error checking for OTA updates:', error);
          logger.info('[MOBILE] Fallback: User should download new version from store');
          setShowStoreUpdateModal(true);
        }
      }

      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription?.remove();
  }, [pendingUpgrade]);

  const dismissStoreUpdate = () => {
    if (updateInfo?.isMandatory) {
      logger.warn('[MOBILE] Cannot dismiss mandatory update');
      return;
    }
    logger.info('[MOBILE] User dismissed optional store update');
    setShowStoreUpdateModal(false);
  };

  return {
    pendingUpgrade,
    isPolling,
    checkForUpdates,
    showStoreUpdateModal,
    updateInfo,
    dismissStoreUpdate,
  };
};

